import React from 'react';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { TimeEntry } from '../../services/api';
import { addDays, getLocalDateString } from '../../utils/dateHelpers';

interface WeekViewProps {
  weekStart: Date;                              // Sunday of the visible week
  entriesByDate: Map<string, TimeEntry[]>;      // YYYY-MM-DD → entries (for this week)
  todayIso: string;                             // local-today YYYY-MM-DD
  onSelectDate: (date: string) => void;         // Switch to Day view on a date
  onEditEntry: (entry: TimeEntry) => void;      // Open edit modal
  onNewEntry: (date: string) => void;           // Open new-entry modal pre-filled with date
  isLoading: boolean;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const sumHours = (entries: TimeEntry[]): number =>
  entries.reduce((total, e) => total + Number(e.hours || 0), 0);

export const WeekView: React.FC<WeekViewProps> = ({
  weekStart,
  entriesByDate,
  todayIso,
  onSelectDate,
  onEditEntry,
  onNewEntry,
  isLoading,
}) => {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekStart, i);
    const iso = getLocalDateString(d);
    return {
      iso,
      date: d,
      dayName: DAY_NAMES[d.getDay()],
      dayShort: DAY_SHORT[d.getDay()],
      entries: entriesByDate.get(iso) || [],
      isToday: iso === todayIso,
    };
  });

  const weekTotal = days.reduce((sum, d) => sum + sumHours(d.entries), 0);

  return (
    <div className="space-y-3">
      {/* Week total */}
      <div className="flex justify-end">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Week total:
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {weekTotal.toFixed(2)}h
          </span>
        </span>
      </div>

      {isLoading && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
          Loading…
        </div>
      )}

      {days.map((day) => {
        const dayTotal = sumHours(day.entries);
        return (
          <div
            key={day.iso}
            className={
              'bg-white dark:bg-gray-800 rounded-xl shadow-sm border transition-colors ' +
              (day.isToday
                ? 'border-green-400 dark:border-green-600'
                : 'border-gray-200 dark:border-gray-700')
            }
          >
            {/* Day header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <button
                type="button"
                onClick={() => onSelectDate(day.iso)}
                className="flex items-baseline gap-3 text-left hover:text-green-700 dark:hover:text-green-400 transition-colors"
                title="Open day view"
              >
                <span className={
                  'text-sm font-semibold ' +
                  (day.isToday ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-white')
                }>
                  {day.dayName}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                {day.isToday && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                    Today
                  </span>
                )}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onNewEntry(day.iso)}
                  className="p-1 text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title={`Add entry for ${day.dayShort}`}
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
                <span className={
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                  (dayTotal > 0
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400')
                }>
                  {dayTotal.toFixed(2)}h
                </span>
              </div>
            </div>

            {/* Entry rows */}
            {day.entries.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500 italic">
                No entries
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700/70">
                {day.entries.map((entry) => (
                  <button
                    type="button"
                    key={entry.id}
                    onClick={() => onEditEntry(entry)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group flex items-center justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {entry.start_time && (
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-400 flex-shrink-0">
                            {entry.start_time}
                          </span>
                        )}
                        <span className="text-sm text-gray-900 dark:text-white truncate">
                          {entry.description}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {entry.category && (
                          <span className="truncate">{entry.category}</span>
                        )}
                        {entry.subproject_name && (
                          <>
                            <span>·</span>
                            <span className="truncate">{entry.subproject_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {Number(entry.hours).toFixed(2)}h
                      </span>
                      <PencilIcon className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-300" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
